/**
 * SegmentedPills — a labeled, single-select pill group with an UNMISTAKABLE
 * active state (solid tone fill + contrast text, like Badge's solid tones).
 * Generic: any string-union value, optional icon and count per option.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export type SegmentedPillTone =
  | 'primary'
  | 'warning'
  | 'success'
  | 'destructive'
  | 'contrast';

export interface SegmentedPillOption<V extends string> {
  value: V;
  label: string;
  icon?: LucideIcon;
  count?: number;
  /** Active fill tone (default `primary`). */
  tone?: SegmentedPillTone;
  /** Shown but not pickable; `title` says why (hover). */
  disabled?: boolean;
  title?: string;
}

const ACTIVE_TONE: Record<SegmentedPillTone, string> = {
  primary: 'bg-primary text-primary-foreground shadow-sm',
  warning: 'bg-warning text-warning-foreground shadow-sm',
  success: 'bg-success text-success-foreground shadow-sm',
  destructive: 'bg-destructive text-destructive-foreground shadow-sm',
  contrast: 'bg-foreground text-background shadow-sm',
};

function Pill<V extends string>({
  option,
  active,
  onSelect,
}: {
  option: SegmentedPillOption<V>;
  active: boolean;
  onSelect: () => void;
}) {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={option.disabled ? undefined : onSelect}
      aria-pressed={active}
      aria-disabled={option.disabled || undefined}
      title={option.title}
      className={cn(
        'inline-flex items-center gap-1 rounded px-2.5 py-1 text-[11px] transition-colors',
        active
          ? cn('font-semibold', ACTIVE_TONE[option.tone ?? 'primary'])
          : option.disabled
            ? 'cursor-not-allowed font-medium text-muted-foreground/50'
            : 'font-medium text-muted-foreground hover:bg-background hover:text-foreground',
      )}
    >
      {Icon ? <Icon className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {option.label}
      {option.count != null ? (
        <span
          className={cn(
            'rounded-full px-1.5 text-[10px] font-semibold tabular-nums',
            active ? 'bg-background/25' : 'bg-muted',
          )}
        >
          {option.count}
        </span>
      ) : null}
    </button>
  );
}

export interface SegmentedPillsProps<V extends string> {
  /** Small uppercase group label rendered before the pills. */
  label?: string;
  options: ReadonlyArray<SegmentedPillOption<V>>;
  value: V;
  onChange: (value: V) => void;
  className?: string;
}

export function SegmentedPills<V extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: SegmentedPillsProps<V>) {
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {label ? (
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      ) : null}
      <div className="inline-flex rounded-md border border-border/60 bg-muted/30 p-0.5">
        {options.map((option) => (
          <Pill
            key={option.value}
            option={option}
            active={value === option.value}
            onSelect={() => onChange(option.value)}
          />
        ))}
      </div>
    </div>
  );
}

export default SegmentedPills;
